// packages/observability/src/redact-token-urls.ts
//
// Bearer tokens that ride in URLs: the public signing/share routes carry one
// in the path, and presigned storage links carry one in the query. A live W-9
// signing token opens the signer's view and, for 7 days after signing, mints
// a download link to a PDF with the payee's full SSN/EIN. Everything that
// ships URLs off the app (PostHog in apps/web, OTel spans and logs here)
// rewrites them through redactTokenUrl first. Pure: safe in the browser, on
// the server, and in tests.
//
// A token URL can also sit percent-encoded inside another string — an
// `sms:…?&body=` link carrying a signing link, a `?next=` param — where every
// `/` reads `%2F` (or `%252F` once more encoded). Both forms are redacted.

// Routes whose next path segment is the bearer token.
const TOKEN_ROUTE_PREFIXES = [
  "sign/w9",
  "sign/1099",
  "share/estimate",
  "share/invoice",
  "share/receipt",
  "share/project",
  "share/plan",
  "share/change-order",
  "share/company",
  "pay",
  "d",
  "invite",
  "reviews",
  "api/documents/sign",
  "api/share",
] as const;

/** The prefixes as one alternation, their `/` spelled `separator`. */
// ponytail: prefixes are literal path segments, so no escaping. This file ships
// to the browser, where RegExp.escape is not baseline; a metacharacter added
// here later would only widen what gets redacted.
function prefixAlternation(separator: string): string {
  return TOKEN_ROUTE_PREFIXES.map((prefix) =>
    prefix.split("/").join(separator),
  ).join("|");
}

// The segment after the route prefix is the token — unless it is a Next.js
// route parameter (`[token]` in next.route / http.route), which is a pattern
// and stays as is so traces keep grouping by route.
const TOKEN_ROUTE = new RegExp(
  `(\\/(?:${prefixAlternation("\\/")})\\/)(?!\\[)[^/?#]+`,
  "g",
);

// Non-global twin of TOKEN_ROUTE, sharing its source. RegExp.prototype.test
// on a global regex is stateful via lastIndex — it advances past each match,
// so calling .test() with the same matching input twice alternates
// true/false. isTokenRoute needs to stay pure across repeated calls, so it
// tests against a regex with no `g` flag instead of reusing TOKEN_ROUTE.
const TOKEN_ROUTE_TEST = new RegExp(TOKEN_ROUTE.source);

// A `/` percent-encoded once or more: `%2F`, `%252F`, `%25252F`…
const ENCODED_SLASH = "%(?:25)*2F";

// The same routes with encoded slashes. The token runs to the next encoded
// character (`%…`) or a raw delimiter; an encoded `[` (`%5B`) is a route
// parameter again. Case-insensitive: `%2f` is as valid as `%2F`.
const ENCODED_TOKEN_ROUTE = new RegExp(
  `(${ENCODED_SLASH}(?:${prefixAlternation(ENCODED_SLASH)})${ENCODED_SLASH})(?!%(?:25)*5B)[^%/?#&"'\\s\\\\]+`,
  "gi",
);

// The credential params of a presigned download link (SigV4) or a storage
// signed URL (`token`): whoever holds one reads the object — for a signed
// W-9, a PDF with the payee's full SSN/EIN. Matched wherever a URL sits
// inside a string (autocapture's $elements_chain wraps the href in quotes,
// hence `"` and `'` in the stop set); the name stays, the value goes.
const CREDENTIAL_NAMES =
  "x-amz-signature|x-amz-credential|x-amz-security-token|token";
const CREDENTIAL_PARAM = new RegExp(
  `([?&#](?:${CREDENTIAL_NAMES})=)[^&#"'\\s\\\\]*`,
  "gi",
);

// The same params percent-encoded (`%3FX-Amz-Signature%3D…`). A value runs
// until the next encoded `&` or `#` — it may itself hold encoded characters
// (a session token's `%2B`, a credential's `%2F`), so `%` alone can't end it.
const ENCODED_CREDENTIAL_PARAM = new RegExp(
  `(%(?:25)*(?:3F|26|23)(?:${CREDENTIAL_NAMES})%(?:25)*3D)(?:(?!%(?:25)*(?:26|23))[^&#"'\\s\\\\])*`,
  "gi",
);

// Telegram Bot API calls carry the bot's whole credential in the path:
// https://api.telegram.org/bot<id>:<secret>/sendMessage (and /file/bot…/).
// A fetch span names itself after that URL, so every alert the prober sent
// wrote the monitoring bot's token into SigNoz (2026-10-26).
const TELEGRAM_BOT_TOKEN = /(\/bot)\d+:[\w-]+/g;

// Signing pages whose link carries the token after `#` rather than in the
// path: /sign/w9#token=… and /sign/1099#token=…. No server ever receives a
// fragment, but the page's own location.href holds the token until the page
// strips it, so these paths are token routes too. The token itself is
// redacted as the `token` credential param above — raw (`#token=`) or
// encoded (`%23token%3D`, inside an `sms:` body).
const FRAGMENT_TOKEN_PAGE = /^\/sign\/(?:w9|1099)\/?$/;

/** `value` with every token-route segment and credential query value redacted. */
export function redactTokenUrl(value: string): string {
  return value
    .replace(TOKEN_ROUTE, "$1:token")
    .replace(ENCODED_TOKEN_ROUTE, "$1:token")
    .replace(CREDENTIAL_PARAM, "$1:redacted")
    .replace(ENCODED_CREDENTIAL_PARAM, "$1:redacted")
    .replace(TELEGRAM_BOT_TOKEN, "$1:token");
}

/**
 * True when `pathname` is a bearer-token route (signing/share links),
 * including the signing pages whose token rides in the fragment. These are
 * always entered by a fresh page load from an email/SMS link, so gating on
 * the initial pathname is enough — there's no in-session navigation onto a
 * token route to also catch.
 */
export function isTokenRoute(pathname: string): boolean {
  return (
    TOKEN_ROUTE_TEST.test(pathname) ||
    FRAGMENT_TOKEN_PAGE.test(pathname) ||
    /^\/weekly(?:\/|$)/.test(pathname)
  );
}
